// modules/Battle/BattleTurnManager.js
// 战斗回合管理模块 - 管理玩家回合和敌人回合的切换

import DebugHelper from '../DebugHelper.js';
import MessageHelper from '../MessageHelper.js';
import SettingsHelper from '../SettingsHelper.js';
import { playSceneBgm } from '../LazyAssetLoader.js';
import { GAME_CONSTANTS } from '../GameConstants.js';
import { checkIfMyTurn } from './BattleUtils.js';

export default class BattleTurnManager {
    constructor(scene, battlePlayer, battleEnemy, battleAnimation, battleUI, battleLogic, battleState, battleReward) {
        this.scene = scene;
        this.battlePlayer = battlePlayer;
        this.battleEnemy = battleEnemy;
        this.battleAnimation = battleAnimation;
        this.battleUI = battleUI;
        this.battleLogic = battleLogic;
        this.battleState = battleState;
        this.battleReward = battleReward;
    }
    
    /**
     * 开始玩家回合
     * @param {boolean} isTeamBattle - 是否是组队模式
     * @param {Array} teamMembers - 队伍成员
     * @param {number} currentTeamMemberIndex - 当前队员索引
     * @param {Function} processPlayerStatusEffects - 处理玩家状态效果的回调
     */
    startPlayerTurn(isTeamBattle = false, teamMembers = [], currentTeamMemberIndex = 0, processPlayerStatusEffects = null) {
        // 如果是组队模式，检查是否所有队员都完成了回合
        if (isTeamBattle && currentTeamMemberIndex >= teamMembers.length) {
            // 所有队员都完成了回合，进入敌人回合
            this.startEnemyTurn();
            return;
        }
        
        this.battleState.phase = 'playerTurn';
        this.battleState.isAnimating = false;
        this.battleState.selectedTarget = null;
        this.battleState.rangeAttackMode = false;
        
        // 如果是组队模式，显示当前操作的队员信息
        if (isTeamBattle && teamMembers.length > 0) {
            const networkManager = window.networkManager;
            const localPlayerId = networkManager ? networkManager.playerId : null;
            const turnCheck = checkIfMyTurn(isTeamBattle, teamMembers, currentTeamMemberIndex, localPlayerId);
            
            if (turnCheck.isMyTurn) {
                // 是我的回合，正常操作
                this._startPlayerTurnCore(processPlayerStatusEffects);
                
                // 显示当前队员信息
                if (teamMembers.length > 1) {
                    this.battleUI.showMessage(`${turnCheck.currentMember?.playerName || '玩家'} 的回合 (${currentTeamMemberIndex + 1}/${teamMembers.length})`);
                }
            } else {
                // 不是我的回合，等待其他队员操作
                this.battleState.phase = 'waitingForTeamMember';
                this.battleUI.showMessage(`等待 ${turnCheck.currentMember?.playerName || '其他玩家'} 操作...`);
                // 清除菜单选中状态并禁用UI交互
                this.battleUI.menu.resetSelection();
                this.battleUI.setInteractive(false);
            }
        } else {
            // 单人模式，正常流程
            this._startPlayerTurnCore(processPlayerStatusEffects);
        }
    }
    
    /**
     * 开始玩家回合的核心逻辑（提取重复代码）
     * @param {Function} processPlayerStatusEffects - 处理玩家状态效果的回调
     * @private
     */
    _startPlayerTurnCore(processPlayerStatusEffects) {
        const player = this.battlePlayer.getPlayer();

        if (player.stunned && player.stunnedTurns > 0) {
            player.stunnedTurns--;
            if (player.stunnedTurns <= 0) {
                player.stunned = false;
            }
            this.battleUI.showMessage('眩晕中，无法行动！', '#ffcc00');
            this.battleUI.setInteractive(false);
            this.scene.events.emit('playerStunnedSkipTurn');
            return;
        }

        if (player.cost < player.maxCost) {
            player.cost = Math.min(player.maxCost, player.cost + 10);
        }
        
        // 处理玩家状态效果
        if (processPlayerStatusEffects) {
            processPlayerStatusEffects();
        }
        
        this.battleUI.showPlayerTurn();
        this.battleUI.setInteractive(true);
    }
    
    /**
     * 开始敌人回合
     * @param {Function} processEnemyStatusEffects - 处理敌人状态效果的回调
     * @param {Function} onDefeat - 失败回调
     * @param {Function} onEnemyAttack - 敌人攻击回调（由调用者提供执行逻辑）
     * @param {Function} onEnemyTurnComplete - 敌人回合完成回调（所有敌人攻击完成后调用）
     */
    startEnemyTurn(processEnemyStatusEffects = null, onDefeat = null, onEnemyAttack = null, onEnemyTurnComplete = null) {
        this.battleState.phase = 'enemyTurn';
        this.battleUI.showEnemyTurn();
        
        // 先处理敌人状态效果
        if (processEnemyStatusEffects) {
            try {
                processEnemyStatusEffects();
            } catch (error) {
                DebugHelper.error('BattleTurnManager: 处理敌人状态效果时出错', error);
            }
        }
        
        // 然后执行敌人攻击（由调用者提供执行逻辑）
        if (onEnemyAttack) {
            try {
                onEnemyAttack();
            } catch (error) {
                DebugHelper.error('BattleTurnManager: 执行敌人攻击回调时出错', error);
            }
        } else {
            // 默认执行敌人攻击
            const aliveEnemies = this.battleEnemy.getAliveEnemies();
            if (aliveEnemies.length === 0) {
                // 这里需要调用 startPlayerTurn，但这个方法不在 TurnManager 中
                // 所以需要由调用者处理，或者通过回调
                return;
            }
            
            let completedAttacks = 0;
            const totalAttacks = aliveEnemies.length;
            
            aliveEnemies.forEach((enemy, index) => {
                this.scene.time.delayedCall(index * GAME_CONSTANTS.BATTLE.ENEMY_ATTACK_STAGGER_MS, () => {
                    this.battleLogic.executeEnemyTurn(
                        [enemy],
                        this.battlePlayer.getPlayer(),
                        (attacker, damage) => {
                            this.battleAnimation.playEnemyAttackAnimation(
                                attacker,
                                this.battlePlayer.playerSprite,
                                damage,
                                () => {
                                    const result = this.battlePlayer.applyDamage(damage);
                                    this.battleAnimation.showDamage(this.battlePlayer.playerSprite, damage);
                                    
                                    // 如果SP为0需要强制下车，通过事件通知battle.js处理
                                    if (result && result.shouldExitVehicle) {
                                        this.battleUI.showMessage('战车SP耗尽，强制下车！', '#ff0000');
                                        // 触发强制下车事件
                                        this.scene.events.emit('forceExitVehicle', () => {
                                            completedAttacks++;
                                            if (completedAttacks >= totalAttacks) {
                                                if (this.battlePlayer.isDefeated()) {
                                                    if (onDefeat) onDefeat();
                                                } else {
                                                    if (onEnemyTurnComplete) {
                                                        onEnemyTurnComplete();
                                                    }
                                                }
                                            }
                                        });
                                        return;
                                    }
                                    
                                    completedAttacks++;
                                    
                                    if (completedAttacks >= totalAttacks) {
                                        if (this.battlePlayer.isDefeated()) {
                                            if (onDefeat) onDefeat();
                                        } else {
                                            // 敌人回合完成，调用回调继续到玩家回合
                                            if (onEnemyTurnComplete) {
                                                onEnemyTurnComplete();
                                            }
                                        }
                                    }
                                }
                            );
                        }
                    );
                });
            });
        }
    }
    
    /**
     * 检查战斗状态（攻击后）
     * @param {boolean} isTeamBattle - 是否是组队模式
     * @param {Array} teamMembers - 队伍成员
     * @param {number} currentTeamMemberIndex - 当前队员索引
     * @param {Function} onVictory - 胜利回调
     * @param {Function} onDefeat - 失败回调
     * @param {Function} onContinue - 继续战斗回调
     * @param {Function} onEnemyTurn - 敌人回合回调（如果不提供，则不会切换到敌人回合）
     */
    checkBattleStatusAfterAttack(isTeamBattle = false, teamMembers = [], currentTeamMemberIndex = 0, onVictory = null, onDefeat = null, onContinue = null, onEnemyTurn = null) {
        const battleResult = this.battleLogic.checkBattleStatus(
            this.battlePlayer.getPlayer(),
            this.battleEnemy.getAllEnemies()
        );
        
        if (battleResult === 'victory') {
            if (onVictory) onVictory();
        } else if (battleResult === 'defeat') {
            if (onDefeat) onDefeat();
        } else {
            // 如果是组队模式，切换到下一个队员
            if (isTeamBattle) {
                if (onContinue) {
                    onContinue(currentTeamMemberIndex + 1);
                }
            } else {
                // 切换到敌人回合（通过回调让调用者处理）
                if (onEnemyTurn) {
                    onEnemyTurn();
                } else {
                    DebugHelper.warn('BattleTurnManager: checkBattleStatusAfterAttack 需要 onEnemyTurn 回调来切换到敌人回合');
                }
            }
        }
    }
    
    /**
     * 处理逃跑尝试
     * @param {Function} onEscapeSuccess - 逃跑成功回调
     * @param {Function} onEscapeFail - 逃跑失败回调（应该调用 startEnemyTurn 或类似方法）
     */
    handleEscapeAttempt(onEscapeSuccess = null, onEscapeFail = null) {
        // 如果正在动画中，忽略重复的逃跑尝试
        if (this.battleState.isAnimating) {
            return;
        }
        
        // 立即设置动画状态，防止快速连续触发
        this.battleState.isAnimating = true;
        
        const success = this.battleLogic.attemptEscape();
        
        if (success) {
            MessageHelper.showSuccess(this.scene, '成功逃跑！');
            this.scene.time.delayedCall(1000, () => {
                this.battleState.isAnimating = false;
                if (onEscapeSuccess) onEscapeSuccess();
            });
        } else {
            MessageHelper.showWarning(this.scene, '逃跑失败！');
            this.scene.time.delayedCall(1000, () => {
                this.battleState.isAnimating = false;
                // 逃跑失败后，调用回调让调用者处理敌人回合
                // 调用者应该调用 startEnemyTurn 来继续战斗
                if (onEscapeFail) {
                    onEscapeFail();
                }
            });
        }
    }
    
    /**
     * 处理胜利
     * @param {Object} bgm - 背景音乐对象
     * @param {Function} onComplete - 完成回调
     */
    handleVictory(bgm = null, onComplete = null) {
        this.battleState.phase = 'victory';
        
        // 计算奖励
        const rewards = this.battleReward.calculateRewards(this.battleEnemy.getDefeatedEnemies());
        this.battleReward.applyRewards(this.battlePlayer.getPlayer(), rewards);
        
        // 生成包含奖励信息的完整胜利消息
        const rewardMessage = this.battleReward.showRewardMessage(rewards);
        
        // 停止当前BGM
        if (bgm) {
            bgm.stop();
        }
        
        // 播放BGM：如果有升级，播放升级BGM，否则播放胜利BGM
        const volume = SettingsHelper.getBGMPlayVolume();
        if (rewards.levelUp) {
            playSceneBgm(this.scene, 'level_up_bgm', { loop: false, volume });
        } else {
            playSceneBgm(this.scene, 'victory_bgm', { loop: false, volume });
        }
        
        // 显示美观的奖励窗口（逐行滚动显示）
        this.battleUI.showRewardWindow(rewardMessage, 400, () => {
            // 奖励窗口关闭后，执行完成回调
            if (onComplete) onComplete(true, rewards);
        });
    }
    
    /**
     * 处理失败
     * @param {Function} onComplete - 完成回调
     */
    handleDefeat(onComplete = null) {
        this.battleState.phase = 'defeat';
        this.battleUI.showDefeat();
        
        this.scene.time.delayedCall(2000, () => {
            if (onComplete) onComplete(false);
        });
    }
}

